import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { format, startOfWeek, addDays, isSameDay, parseISO, isWithinInterval } from 'date-fns';

const ResourceCalendarView = ({ resources, filters }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week');

  const weekDates = useMemo(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 0 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  }, [currentDate]);

  const monthDates = useMemo(() => {
    if (viewMode !== 'month') return [];
    const year = currentDate?.getFullYear();
    const month = currentDate?.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPad = firstDay?.getDay();
    const dates = [];
    for (let i = 0; i < startPad; i++) dates?.push(null);
    for (let d = 1; d <= lastDay?.getDate(); d++) dates?.push(new Date(year, month, d));
    return dates;
  }, [currentDate, viewMode]);

  // Apply status filter to resources (same logic as List view)
  const filteredResources = useMemo(() => {
    let result = [...(resources || [])];
    const status = filters?.status;
    if (status && status !== 'all') {
      result = result?.filter(resource => {
        if (status === 'conflicts') {
          return resource?.conflicts && resource?.conflicts?.length > 0;
        }
        // Map filter values to resource status values
        const statusMap = {
          'available': 'Available',
          'in-use': 'In Use',
          'maintenance': 'Maintenance',
          'unavailable': 'Unavailable',
        };
        const expectedStatus = statusMap?.[status] || status;
        return resource?.status === expectedStatus;
      });
    }
    return result;
  }, [resources, filters?.status]);

  const navigate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'week') {
      newDate?.setDate(currentDate?.getDate() + direction * 7);
    } else {
      newDate?.setMonth(currentDate?.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const getAssignmentsForResourceOnDate = (resource, date) => {
    if (!date) return [];
    return (resource?.assignments || [])?.filter(assignment => {
      if (!assignment?.startDate || !assignment?.endDate) return false;
      try {
        const start = typeof assignment?.startDate === 'string' ? parseISO(assignment?.startDate) : new Date(assignment.startDate);
        const end = typeof assignment?.endDate === 'string' ? parseISO(assignment?.endDate) : new Date(assignment.endDate);
        return isWithinInterval(date, { start, end });
      } catch {
        return false;
      }
    });
  };

  const displayDates = viewMode === 'week' ? weekDates : monthDates;
  const displayResources = filteredResources?.slice(0, 20);

  const headerLabel = viewMode === 'week'
    ? `${format(weekDates?.[0], 'MMM d')} – ${format(weekDates?.[6], 'MMM d, yyyy')}`
    : format(currentDate, 'MMMM yyyy');

  const getResourceStatusDot = (resource) => {
    if (resource?.conflicts?.length > 0) return 'bg-red-500';
    if (resource?.status === 'Available') return 'bg-green-500';
    if (resource?.status === 'In Use') return 'bg-blue-500';
    return 'bg-gray-400';
  };

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      {/* Calendar Controls */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
            <Icon name="ChevronLeft" size={16} />
          </Button>
          <span className="text-sm font-semibold text-foreground px-2 min-w-[160px] text-center">
            {headerLabel}
          </span>
          <Button variant="outline" size="sm" onClick={() => navigate(1)}>
            <Icon name="ChevronRight" size={16} />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>
            Today
          </Button>
        </div>
        <div className="flex items-center gap-3">
          {filters?.status && filters?.status !== 'all' && (
            <span className="text-xs text-muted-foreground bg-accent/10 text-accent px-2 py-1 rounded-full font-medium">
              Filtered: {filters?.status === 'in-use' ? 'In Use' : filters?.status === 'conflicts' ? 'Has Conflicts' : filters?.status?.charAt(0)?.toUpperCase() + filters?.status?.slice(1)}
            </span>
          )}
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            <button
              onClick={() => setViewMode('week')}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                viewMode === 'week' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setViewMode('month')}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                viewMode === 'month' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Month
            </button>
          </div>
        </div>
      </div>
      {/* Week View */}
      {viewMode === 'week' && (
        <div className="overflow-x-auto">
          <div style={{ minWidth: '700px' }}>
            {/* Day headers */}
            <div className="grid border-b border-border bg-muted/50" style={{ gridTemplateColumns: '200px repeat(7, 1fr)' }}>
              <div className="px-4 py-3 border-r border-border">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Resource</span>
              </div>
              {weekDates?.map((date, i) => {
                const isToday = isSameDay(date, new Date());
                return (
                  <div key={i} className={`px-2 py-3 border-r border-border last:border-r-0 text-center ${isToday ? 'bg-accent/10' : ''}`}>
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      {format(date, 'EEE')}
                    </div>
                    <div className={`text-xs mt-1 font-medium ${isToday ? 'text-accent' : 'text-muted-foreground'}`}>
                      {format(date, 'MMM d')}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Resource rows */}
            {displayResources?.length === 0 ? (
              <div className="p-12 text-center">
                <Icon name="Calendar" size={40} className="mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No resources match the current filter</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {displayResources?.map((resource) => (
                  <div key={resource?.id} className="grid hover:bg-muted/20 transition-colors" style={{ gridTemplateColumns: '200px repeat(7, 1fr)' }}>
                    <div className="px-4 py-3 border-r border-border bg-background">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${getResourceStatusDot(resource)}`} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{resource?.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{resource?.category}</p>
                        </div>
                        {resource?.conflicts?.length > 0 && (
                          <span className="ml-auto flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 text-xs font-medium">
                            <Icon name="AlertTriangle" size={10} />
                            {resource?.conflicts?.length}
                          </span>
                        )}
                      </div>
                    </div>
                    {weekDates?.map((date, i) => {
                      const dayAssignments = getAssignmentsForResourceOnDate(resource, date);
                      const isToday = isSameDay(date, new Date());
                      return (
                        <div key={i} className={`px-1 py-2 border-r border-border last:border-r-0 min-h-[56px] ${isToday ? 'bg-accent/5' : ''}`}>
                          {dayAssignments?.map((assignment, idx) => (
                            <div
                              key={assignment?.id || idx}
                              className="rounded px-1.5 py-0.5 text-xs font-medium mb-1 bg-blue-500/20 text-blue-700 border border-blue-500/30 truncate"
                              title={`${assignment?.projectName || 'Project'}${assignment?.phaseName ? ` – ${assignment?.phaseName}` : ''}`}
                            >
                              {assignment?.projectName || 'Assigned'}
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {/* Month View */}
      {viewMode === 'month' && (
        <div className="overflow-x-auto">
          <div style={{ minWidth: '700px' }}>
            {/* Day-of-week headers */}
            <div className="grid grid-cols-7 border-b border-border bg-muted/50">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']?.map(d => (
                <div key={d} className="px-2 py-2 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider border-r border-border last:border-r-0">
                  {d}
                </div>
              ))}
            </div>
            {/* Calendar grid */}
            <div className="grid grid-cols-7">
              {monthDates?.map((date, i) => {
                const isToday = date && isSameDay(date, new Date());
                const allAssignments = date
                  ? displayResources?.flatMap(r => getAssignmentsForResourceOnDate(r, date)?.map(a => ({ ...a, resourceName: r?.name })))
                  : [];
                return (
                  <div
                    key={i}
                    className={`min-h-[80px] border-r border-b border-border last:border-r-0 p-1 ${
                      !date ? 'bg-muted/30' : isToday ? 'bg-accent/5' : 'bg-background'
                    }`}
                  >
                    {date && (
                      <>
                        <div className={`text-xs font-semibold mb-1 ${isToday ? 'text-accent' : 'text-foreground'}`}>
                          {format(date, 'd')}
                        </div>
                        {allAssignments?.slice(0, 3)?.map((a, idx) => (
                          <div
                            key={idx}
                            className="rounded px-1 py-0.5 text-xs mb-0.5 bg-blue-500/20 text-blue-700 border border-blue-500/30 truncate"
                            title={`${a?.resourceName}: ${a?.projectName || 'Project'}`}
                          >
                            {a?.resourceName}
                          </div>
                        ))}
                        {allAssignments?.length > 3 && (
                          <div className="text-xs text-muted-foreground">+{allAssignments?.length - 3} more</div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {/* Legend */}
      <div className="px-4 py-3 border-t border-border flex flex-wrap items-center gap-4 text-sm bg-muted/30">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500" />
          <span className="text-muted-foreground text-xs">Available</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-500" />
          <span className="text-muted-foreground text-xs">In Use</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <span className="text-muted-foreground text-xs">Has Conflicts</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-blue-500/20 border border-blue-500/30" />
          <span className="text-muted-foreground text-xs">Assigned</span>
        </div>
        {(resources?.length || 0) > 20 && (
          <span className="text-xs text-muted-foreground ml-auto">Showing first 20 of {filteredResources?.length} resources</span>
        )}
      </div>
    </div>
  );
};

export default ResourceCalendarView;